import { keepPreviousData } from "@tanstack/react-query";
import classNames from "classnames";
import { ChatListItem } from "PFApp/components/chat/chat_list_item";
import { ChatListMenu } from "PFApp/components/chat/chat_list_menu";
import { ChatSubheader } from "PFApp/components/chat/chat_subheader";
import { CHAT_NOTIFICATION_CATEGORIES } from "PFApp/constants/notifications";
import { ActionIcon } from "PFComponents/action_icon";
import { Inline } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { LoadingDots } from "PFComponents/loading_dots";
import { getTargetType } from "PFCore/helpers/conversation";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { useChatConversations } from "PFCore/hooks/queries/chat/use_chat_conversations";
import { useNotificationsMarkAsRead } from "PFCore/hooks/queries/notifications/use_notifications_mark_as_read";
import { fetchParticipants } from "PFCore/services/chat/fetch_participants";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./chat.module.scss";
import cssList from "./chat_list.module.scss";

const CHAT_LIST_PAGE_SIZE = 15;

const canFetchMore = (conversations, total) => (conversations || []).length < total;

type ChatListProps = {
  onMinimize: (id: string) => void;
  onOpenConversation: (id: number) => void;
  onUnMinimize: (id: string) => void;
  onClearNotifications: () => void;
  openConversationIds: number[];
  style: React.CSSProperties;
};

export const ChatList = ({
  onMinimize,
  onOpenConversation,
  onUnMinimize,
  onClearNotifications,
  openConversationIds,
  style
}: ChatListProps) => {
  const { t } = useTranslation("core", { keyPrefix: "chat" });
  const [participants, setParticipants] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const [term, setTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [page, setPage] = useState(1);

  const {
    data: conversations,
    isFetching,
    isFetched
  } = useChatConversations(
    {
      empty: false,
      me: true,
      perPage: CHAT_LIST_PAGE_SIZE * page,
      term
    },
    {
      placeholderData: keepPreviousData
    }
  );

  const { mutateAsync: markNotificationsAsRead } = useNotificationsMarkAsRead();

  useEffect(() => {
    if (!conversations) {
      return;
    }

    conversations.entries.forEach((conversation) => {
      if (!participants[conversation.id]) {
        fetchParticipants({
          perPage: 5,
          conversationId: conversation.id,
          withMessage: getTargetType(conversation) !== "Profile"
        }).then((data) => {
          setParticipants((participants) => ({
            ...participants,
            [conversation.id]: data
          }));
        });
      }
    });
  }, [conversations]);

  const handleSearch = useDebounce(() => {
    setTerm(searchValue);
  }, 500);

  const handleUnMinimize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onUnMinimize?.("list");
  };

  const handleMinimize = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    onMinimize?.("list");
    setDropdownOpen(false);
  };

  const handleScroll = (e) => {
    const el = e.target;

    if (el.scrollHeight - el.scrollTop < el.clientHeight + 50) {
      if (isFetched && canFetchMore(conversations?.entries, conversations?.meta?.total)) {
        setPage(page + 1);
      }
    }
  };

  const handleMarkAllRead = async () => {
    markNotificationsAsRead({ categories: CHAT_NOTIFICATION_CATEGORIES }).then(() => onClearNotifications());
  };

  const isEmpty = conversations?.entries?.length === 0;

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  return (
    <div
      className={classNames(css.root, css.selected, css.expanded, css.list)}
      style={style}
      data-qa-id="chat-root"
    >
      <div
        role="button"
        title={t("allChats")}
        className={cssList.header}
        onClick={handleUnMinimize}
        onKeyDown={handleUnMinimize}
        data-qa-id="chat-list-header"
        tabIndex={0}
      >
        <div className={cssList.headerActions}>
          <Inline alignItems="center" className={cssList.actionsTitle} gap="spacingLg">
            <Icon name="chat" color="paletteWhite0" size="sm" />
            <span>{t("allChats")}</span>
          </Inline>
          <Inline alignItems="center" className={cssList.actions} gap="spacingSm">
            <ActionIcon
              name="subtract"
              onClick={handleMinimize}
              color="paletteWhite0"
              size="sm"
              disableHover
            />
            <ActionIcon
              name="menu-horizontal"
              onClick={toggleDropdown}
              color="paletteWhite0"
              size="sm"
              disableHover
            />
          </Inline>
        </div>
        {dropdownOpen && (
          <ChatListMenu
            markAllRead={handleMarkAllRead}
            handleChange={(fn) => {
              setDropdownOpen(false);
              fn();
            }}
          />
        )}
      </div>
      {/* @ts-ignore */}
      <ChatSubheader>
        <input
          value={searchValue}
          placeholder={t("searchTitleOrPeople")}
          className={cssList.input}
          onChange={(e) => {
            const searchValue = e.target.value;
            setSearchValue(searchValue);
            handleSearch();
          }}
        />
        <Icon name="search" size="lg" className={cssList.searchIcon} />
      </ChatSubheader>
      <div className={css.bodyWrap}>
        <div className={cssList.wrap} onScroll={handleScroll}>
          {isFetched && isEmpty && (
            <div className={cssList.wrapEmpty}>
              <div>
                <Icon name="chat" color="paletteWhite3" size="xxxl" />
                <div>{t("noConversations")}</div>
              </div>
            </div>
          )}
          {!isEmpty && (
            <>
              <ul className={cssList.list}>
                {conversations?.entries?.map((conversation) => (
                  <ChatListItem
                    isOpen={openConversationIds.indexOf(conversation.id) >= 0}
                    key={conversation.id}
                    handleOpenConversation={onOpenConversation}
                    conversation={conversation}
                    participants={participants[conversation.id]?.entries || []}
                  />
                ))}
              </ul>
            </>
          )}
          {isFetching && (
            <LoadingDots circleSize="xxxxs" circlesEnabled centered className={cssList.loading} />
          )}
        </div>
      </div>
    </div>
  );
};
